// OrdersLineChart.jsx
import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ordersApi } from "components/Apis/orders";
import LoadingAnimation from "components/UI/Loader";
// import "./chart-colors.css"; // <-- ensure the SCSS/CSS with your classes is loaded somewhere

// --- shape lane/day API → Apex series/categories ---
function laneDailyToApex(rows) {
  const data = Array.isArray(rows) ? rows : [];
  if (!data.length) return { categories: [], series: [], dayKeys: [] };

  const daySet = new Set();
  const lanesMap = new Map();

  data.forEach((row) => {
    if (!row?.day) return;

    const laneId = row?.lane__id;
    const laneName = row?.lane || "Unassigned";
    const laneKey = laneId ?? `null-${laneName}`;
    const day = row.day;
    const totalOrders = Number(row?.total_orders) || 0;

    daySet.add(day);

    if (!lanesMap.has(laneKey)) {
      lanesMap.set(laneKey, { id: laneId, name: laneName, points: new Map() });
    }

    const laneEntry = lanesMap.get(laneKey);
    const previousCount = laneEntry.points.get(day) || 0;
    laneEntry.points.set(day, previousCount + totalOrders);
  });

  const days = Array.from(daySet).sort();
  const categories = days;

  const sortedLanes = Array.from(lanesMap.values()).sort((a, b) => {
    const aId = typeof a.id === "number" ? a.id : Number.MAX_SAFE_INTEGER;
    const bId = typeof b.id === "number" ? b.id : Number.MAX_SAFE_INTEGER;
    if (aId !== bId) return aId - bId;
    return a.name.localeCompare(b.name);
  });

  const series = sortedLanes.map((lane) => ({
    name: lane.name,
    data: days.map((day) => lane.points.get(day) ?? 0),
  }));

  return { categories, series, dayKeys: days };
}

// --- color helpers (CSS-driven) ---
const FALLBACK_PALETTE = [
  "#77B6EA", "#545454", "#00A67E", "#FF7A59",
  "#A14EF0", "#F2C94C", "#2D9CDB", "#EB5757",
];

const LANE_COLOR_MAP = {
  MXINTRA: "#6f42c1",
  MXSB: "#ffc107",
  MXNB: "#dc3545",
  "0MILES": "#20c997",
  DTW: "#17a2b8",
  DTWSB: "#138496",
  LRDNB: "#fd7e14",
  CKVSBB: "#e83e8c",
  CKVNBB: "#c82333",
  DIRECT: "#007bff",
  UNASSIGNED: "#6c757d",
};

// "Clarksville" -> "clarksville-background"
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}
function classForSeriesName(name) {
  return `${slugify(name)}-background`;
}

function normalizeLaneName(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function colorForLane(name, idx) {
  const normalized = normalizeLaneName(name);
  return (
    LANE_COLOR_MAP[normalized] ||
    colorFromClass(classForSeriesName(name)) ||
    FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length]
  );
}

function formatDay(day) {
  if (!day) return "";
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
}

function formatDayLong(day) {
  if (!day) return "";
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/**
 * Create a hidden probe element with a class and read its computed style.
 * Tries background-color first (since your classes define background), then color.
 */
function colorFromClass(className) {
  if (typeof window === "undefined" || !document?.body) return null; // SSR guard
  const el = document.createElement("span");
  el.className = className;
  Object.assign(el.style, {
    position: "absolute",
    width: "0",
    height: "0",
    overflow: "hidden",
    pointerEvents: "none",
    visibility: "hidden",
  });
  document.body.appendChild(el);
  const styles = getComputedStyle(el);
  const bg = styles.backgroundColor;
  const fg = styles.color;
  document.body.removeChild(el);

  const pick = bg && bg !== "rgba(0, 0, 0, 0)" ? bg : (fg && fg !== "rgba(0, 0, 0, 0)" ? fg : null);
  return pick;
}

const OrdersLineChart = () => {
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dayKeys, setDayKeys] = useState([]);
  const [colors, setColors] = useState([]);

  useEffect(() => {
    (async () => {
      const api = await ordersApi.getLaneVolumePerDay();
      const shaped = laneDailyToApex(api?.data);
      setSeries(shaped.series);
      setCategories(shaped.categories);
      setDayKeys(shaped.dayKeys);
    })();
  }, []);

  // compute CSS-driven colors after series load
  useEffect(() => {
    if (!series.length) return;
    const cols = series.map((s, idx) => colorForLane(s.name, idx));
    setColors(cols);
  }, [series]);

  const laneTotals = useMemo(
    () =>
      series.map((laneSeries) =>
        laneSeries.data.reduce((acc, value) => acc + (Number(value) || 0), 0)
      ),
    [series]
  );

  const annotations = useMemo(() => {
    if (!series.length || !dayKeys.length) return {};

    let peakValue = -1;
    let peakSeriesIndex = 0;
    let peakDayIndex = 0;
    let peakLaneName = "";

    series.forEach((laneSeries, sIdx) => {
      laneSeries.data.forEach((val, dIdx) => {
        if (val > peakValue) {
          peakValue = val;
          peakSeriesIndex = sIdx;
          peakDayIndex = dIdx;
          peakLaneName = laneSeries.name;
        }
      });
    });

    if (peakValue < 0) return {};

    const peakDay = dayKeys[peakDayIndex];
    const laneTotalPoints = series.map((laneSeries, seriesIndex) => {
      const totalOrders = laneTotals[seriesIndex] || 0;
      const lastPointIndex = laneSeries.data.reduce(
        (lastIdx, value, idx) => ((Number(value) || 0) > 0 ? idx : lastIdx),
        -1
      );
      const targetIndex = lastPointIndex >= 0 ? lastPointIndex : dayKeys.length - 1;
      const targetX = dayKeys[targetIndex];
      const targetY = Number(laneSeries.data[targetIndex]) || 0;
      const laneColor = colors[seriesIndex] || "#222";

      return {
        x: targetX,
        y: targetY,
        marker: {
          size: 0,
        },
        label: {
          borderColor: laneColor,
          offsetX: 8,
          style: { color: "#fff", background: laneColor, fontSize: "10px" },
          text: `Total: ${totalOrders}`,
        },
      };
    });

    return {
      xaxis: [
        {
          x: peakDay,
          borderColor: "#222",
          strokeDashArray: 3,
          label: {
            borderColor: "#222",
            style: { color: "#fff", background: "#222" },
            text: `Peak day: ${formatDay(peakDay)}`,
          },
        },
      ],
      points: [
        {
          x: peakDay,
          y: peakValue,
          seriesIndex: peakSeriesIndex,
          marker: {
            size: 5,
            fillColor: colors[peakSeriesIndex] || "#222",
            strokeColor: "#fff",
            radius: 2,
          },
          label: {
            borderColor: colors[peakSeriesIndex] || "#222",
            style: { color: "#fff", background: colors[peakSeriesIndex] || "#222" },
            text: `${peakLaneName}: ${peakValue}`,
          },
        },
        ...laneTotalPoints,
      ],
    };
  }, [colors, dayKeys, laneTotals, series]);

  const options = useMemo(
    () => ({
      chart: {
        height: 350,
        type: "line",
        dropShadow: {
          enabled: true,
          color: "#000",
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.5,
        },
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors, // <- pulled from CSS classes
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" },
      annotations,
      grid: {
        borderColor: "#e7e7e7",
        row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 },
      },
      markers: {
        size: 1,
        colors,        // marker fill
      },
      xaxis: {
        categories,
        labels: {
          formatter: (value, _timestamp, opts) => {
            const index = typeof opts?.i === "number" ? opts.i : categories.indexOf(value);
            return index % 2 === 0 ? formatDay(value) : "";
          },
        },
        title: { text: "Day" },
      },
      yaxis: {
        title: { text: "Order Count" },
        min: 0,
        labels: {
          formatter: (value) => Math.round(value).toString(),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (value) => formatDayLong(value),
        },
        y: {
          formatter: (value) => `${Number(value || 0).toLocaleString()} orders`,
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        floating: true,
        offsetY: -25,
        offsetX: -5,
        formatter: (seriesName, opts) => {
          const total = laneTotals[opts.seriesIndex] || 0;
          return `${seriesName} (${total})`;
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { legend: { position: "bottom", floating: false, offsetY: 0, offsetX: 0 } },
        },
      ],
    }),
    [annotations, categories, colors, laneTotals]
  );

  if (!series.length || !categories.length)
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <LoadingAnimation />
      </div>
    );

  return (
    <ReactApexChart options={options} series={series} type="line" height="100%" />
  );
};

export default OrdersLineChart;
